import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Profile: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'google':
        return '#4285f4';
      case 'facebook':
        return '#1877f2';
      case 'github':
        return '#333';
      case 'email':
        return '#666';
      default:
        return '#666';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Avatar
            src={user.picture}
            sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
          >
            {user.name.charAt(0)}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            {user.name}
          </Typography>
          <Chip
            label={user.role}
            color={user.role === 'admin' ? 'error' : 'primary'}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {user.provider.charAt(0).toUpperCase() + user.provider.slice(1)} Account
            </Typography>
          </Box>
        </Paper>

        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <EmailIcon color="action" />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Email Address
                </Typography>
                <Typography variant="body1">
                  {user.email}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Account Type
                </Typography>
                <Typography variant="body1">
                  {user.provider.charAt(0).toUpperCase() + user.provider.slice(1)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon color="action" />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  User Role
                </Typography>
                <Typography variant="body1">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Security
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Your account is secured through {user.provider} authentication. 
              You can manage your account settings through your {user.provider} account.
            </Typography>
            <Chip
              label={`Connected to ${user.provider}`}
              color="success"
              variant="outlined"
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile; 